// ─── Admin org-analytics: pure helpers ───
// No imports on purpose: tests/admin-analytics.test.mjs imports this file straight
// into Node (same contract as ./orgs/helpers.js).
//
// The one rule that drives most of this file: **every ratio the API returns can be
// null** (zero denominator — see app.pct/app.ratio in migrations/015). A null ratio
// is not 0 and not NaN; it renders as an em dash. Production today has 1 org, 2
// members, 0 programs and 0 messages, so the null path is the common path.

export const DASH = '—';

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

/** 1234567 → "1,234,567"; null/undefined/NaN → "—". Locale-independent on purpose. */
export function numText(v) {
  if (v === true || v === false || !finite(Number(v)) || v === null || v === undefined || v === '') return DASH;
  const n = Number(v);
  const neg = n < 0;
  const [int, frac] = Math.abs(n).toString().split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + grouped + (frac ? '.' + frac : '');
}

/** 42 → "42%", 42.55 → "42.6%", null → "—". */
export function pctText(v, dp = 1) {
  if (v === null || v === undefined || !finite(Number(v))) return DASH;
  const n = Number(v);
  const r = Number.isInteger(n) ? String(n) : String(Math.round(n * 10 ** dp) / 10 ** dp);
  return `${r}%`;
}

/** Ratios that are not percentages (avg clients per coach). null → "—". */
export function ratioText(v, dp = 1) {
  if (v === null || v === undefined || !finite(Number(v))) return DASH;
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10 ** dp) / 10 ** dp);
}

/** Seconds → "45s" / "12m" / "3h" / "6d". null → "—". */
export function ageText(seconds) {
  if (seconds === null || seconds === undefined || !finite(Number(seconds))) return DASH;
  const s = Math.max(0, Math.floor(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/** Share of a total, as a percentage — null when the total is 0 (never 0, never NaN). */
export function share(part, total) {
  const p = Number(part), t = Number(total);
  if (!finite(p) || !finite(t) || t <= 0) return null;
  return Math.round((p / t) * 1000) / 10;
}

// ─── Dates ───
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM-DD' → 'Sep 4'. Parsed by hand so a timezone can never shift the day. */
export function dayLabel(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || ''));
  return m ? `${MON[+m[2] - 1] || '?'} ${+m[3]}` : '';
}
/** 'YYYY-MM' (or 'YYYY-MM-DD') → "Sep '26". */
export function monthLabel(m) {
  const x = /^(\d{4})-(\d{2})/.exec(String(m || ''));
  return x ? `${MON[+x[2] - 1] || '?'} '${x[1].slice(2)}` : '';
}
/** ISO instant → 'Sep 4, 2026'. null → '—'. */
export function dateText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${MON[+m[2] - 1] || '?'} ${+m[3]}, ${m[1]}` : DASH;
}

// ─── Cron heartbeat ───
// The API considers the cron stale after 15 minutes (it is scheduled every 5).
export const HEARTBEAT_STALE_SECONDS = 15 * 60;
/**
 * `heartbeat` from org_overview → a render-ready state.
 * `last_run_at: null` is the current production state (CRON_SECRET unset on the API),
 * which is a misconfiguration worth naming rather than a silent zero.
 */
export function heartbeatState(hb, staleAfter = HEARTBEAT_STALE_SECONDS) {
  if (!hb || !hb.last_run_at) {
    return {
      everRun: false, tone: 'error', label: 'never run', age: DASH,
      detail: 'CRON_SECRET is not set on the API project — seat snapshots, scheduled announcements and queued email are not running.',
    };
  }
  const age = hb.age_seconds;
  const stale = !finite(Number(age)) || Number(age) > staleAfter;
  if (stale) {
    return { everRun: true, tone: 'warn', label: 'stale', age: ageText(age), detail: `Last run ${ageText(age)} ago — the cron is scheduled every 5 minutes.` };
  }
  if (hb.ok === false) {
    return { everRun: true, tone: 'warn', label: 'last run failed', age: ageText(age), detail: String(hb.detail || 'The last run reported a failure.') };
  }
  return { everRun: true, tone: 'ok', label: 'healthy', age: ageText(age), detail: `Last run ${ageText(age)} ago.` };
}

// ─── Range control ───
export const RANGE_DAYS = [7, 30, 90];
export const clampDays = (d) => (RANGE_DAYS.includes(Number(d)) ? Number(d) : 30);

// ─── Consent ───
export const CONSENT_SCOPES = [
  ['workouts', 'Workouts'], ['checkins', 'Check-ins'], ['nutrition', 'Nutrition'],
  ['body', 'Body stats'], ['photos', 'Photos'],
];
/** consent → [{ key, label, pct, count }] — `pct` null when no consent rows exist. */
export function consentRows(consent) {
  const c = consent || {};
  return CONSENT_SCOPES.map(([key, label]) => ({
    key, label,
    pct: c.opt_in_pct ? (c.opt_in_pct[key] ?? null) : null,
    count: Number(c[`share_${key}`]) || 0,
  }));
}
/** consent_events.revoked_by_scope → [{ key, label, count }], zero-filled and ordered. */
export function revokedRows(byScope) {
  const m = byScope || {};
  return CONSENT_SCOPES.map(([key, label]) => ({ key, label, count: Number(m[key]) || 0 }))
    .concat(Object.keys(m).filter(k => !CONSENT_SCOPES.some(([c]) => c === k)).map(k => ({ key: k, label: k, count: Number(m[k]) || 0 })));
}

// ─── Adherence ───
export const ADHERENCE_BUCKETS = [
  ['on_track', 'On track'], ['slipping', 'Slipping'], ['at_risk', 'At risk'], ['inactive', 'Inactive'],
];
/** adherence → [{ key, label, value, pct }] — `pct` null when there are no clients. */
export function adherenceRows(adherence) {
  const a = adherence || {};
  const total = Number(a.clients) || 0;
  return ADHERENCE_BUCKETS.map(([key, label]) => {
    const value = Number(a[key]) || 0;
    return { key, label, value, pct: share(value, total) };
  });
}

// ─── Invites ───
/** invites → the funnel the screen draws: created → used → redemptions. */
export function inviteFunnel(invites) {
  const i = invites || {};
  return [
    { key: 'total', label: 'Created', value: Number(i.total) || 0 },
    { key: 'used', label: 'Used', value: Number(i.used) || 0 },
    { key: 'redemptions', label: 'Redemptions', value: Number(i.redemptions) || 0 },
  ];
}

// ─── Platform series ───
export const SERIES_KEYS = [
  ['org_signups', 'Org signups'], ['member_joins', 'Member joins'],
  ['org_messages', 'Org messages'], ['announcements', 'Announcements'],
  ['workouts_org', 'Workouts (org)'], ['workouts_solo', 'Workouts (solo)'],
];
export const SERIES_COLUMNS = ['date', ...SERIES_KEYS.map(([k]) => k)];
/** series rows → recharts data with a short axis label and every key coerced to a number. */
export function seriesChartData(rows) {
  return (rows || []).map((r) => {
    const out = { date: r.date, label: dayLabel(r.date) };
    for (const [k] of SERIES_KEYS) out[k] = Number(r[k]) || 0;
    return out;
  });
}
/** Column totals over the series — used for the "nothing happened in this window" empty state. */
export function seriesTotals(rows) {
  const out = {};
  for (const [k] of SERIES_KEYS) out[k] = 0;
  for (const r of rows || []) for (const [k] of SERIES_KEYS) out[k] += Number(r[k]) || 0;
  out.all = SERIES_KEYS.reduce((n, [k]) => n + out[k], 0);
  return out;
}

// ─── CSV ───
export const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
/** columns is a list of keys; rows are plain objects. */
export const toCsv = (columns, rows) =>
  [columns.join(','), ...(rows || []).map(r => columns.map(c => csvCell(r[c])).join(','))].join('\n');
/** Several tables in one file, separated by a blank line (Excel and Numbers both cope). */
export const csvBlocks = (blocks) =>
  (blocks || []).filter(b => b && b.columns).map(b => toCsv(b.columns, b.rows)).join('\n\n');

/**
 * Nested counters → [{ metric, value }] with dot paths. Arrays are skipped (they
 * get their own table) and nulls become '' so a null ratio is never exported as 0.
 */
export function flattenMetrics(obj, prefix = '') {
  const out = [];
  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) continue;
    if (v && typeof v === 'object') out.push(...flattenMetrics(v, key));
    else out.push({ metric: key, value: v === null || v === undefined ? '' : v });
  }
  return out;
}

export const ORG_COACH_COLUMNS = ['coach_email', 'clients', 'avg_adherence_pct_28d', 'on_track', 'slipping', 'at_risk', 'inactive'];
export const ORG_GROWTH_COLUMNS = ['month', 'joins', 'departures'];
export const ORG_SEAT_COLUMNS = ['month', 'active_clients', 'member_rows', 'frozen', 'taken_at'];
export const ORG_WEEK_COLUMNS = ['week_start', 'sessions', 'clients'];
export const ORG_LIST_COLUMNS = ['id', 'slug', 'name', 'status', 'members', 'coaches', 'active_clients', 'seat_limit', 'created_at'];

/** Platform view → flat metric table + the daily series table. */
export function platformCsv(overview, series) {
  return csvBlocks([
    { columns: ['metric', 'value'], rows: flattenMetrics(overview || {}) },
    { columns: SERIES_COLUMNS, rows: (series && series.series) || [] },
  ]);
}

/** Per-org view → flat metric table + growth, seats, weeks and coach tables. */
export function orgCsv(analytics) {
  const a = analytics || {};
  return csvBlocks([
    { columns: ['metric', 'value'], rows: flattenMetrics(a) },
    { columns: ORG_GROWTH_COLUMNS, rows: a.growth || [] },
    { columns: ORG_SEAT_COLUMNS, rows: a.seats || [] },
    { columns: ORG_WEEK_COLUMNS, rows: a.weeks || [] },
    { columns: ORG_COACH_COLUMNS, rows: a.coaches || [] },
  ]);
}

/** ironlog-platform-analytics-2026-09-11.csv */
export const csvFilename = (prefix, now = new Date()) =>
  `ironlog-${prefix}-${new Date(now).toISOString().slice(0, 10)}.csv`;

/**
 * Blob-based download. A `data:` URL is size-limited and blocked in some webviews,
 * and an un-revoked object URL pins the Blob for the life of the document — this
 * revokes. `doc`/`urlApi` are injectable so the helper is testable without a DOM.
 * (Same contract as orgs/helpers.js downloadText, kept here because this screen may
 * not import from orgs/.)
 */
export function downloadText(filename, text, type = 'text/csv;charset=utf-8', doc = typeof document !== 'undefined' ? document : null, urlApi = typeof URL !== 'undefined' ? URL : null) {
  if (!doc || !urlApi || typeof Blob === 'undefined') return false;
  const blob = new Blob([text], { type });
  const href = urlApi.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = href; a.download = filename; a.style.display = 'none';
  doc.body.appendChild(a); a.click(); doc.body.removeChild(a);
  setTimeout(() => { try { urlApi.revokeObjectURL(href); } catch { /* already gone */ } }, 1000);
  return true;
}

// ─── Business Value: white-label tab ───
export const FLAG_ADOPTION_COLUMNS = ['key', 'orgs_on', 'orgs_off', 'client_overrides_on', 'client_overrides_off'];
/** flagAdoption rows that actually carry a decision, busiest first; [] when nobody has set one. */
export function flagAdoptionRows(rows) {
  return (rows || [])
    .map(r => ({ ...r, total: (Number(r.orgs_on) || 0) + (Number(r.orgs_off) || 0) + (Number(r.client_overrides_on) || 0) + (Number(r.client_overrides_off) || 0) }))
    .filter(r => r.total > 0)
    .sort((a, b) => b.total - a.total || String(a.key).localeCompare(String(b.key)));
}
/** retention30d → the two comparable bars, with a null pct when the cohort is empty. */
export function retentionRows(r) {
  const x = r || {};
  return [
    { key: 'org', label: 'In an org', active: Number(x.org_active) || 0, total: Number(x.org_total) || 0, pct: x.org_pct ?? null },
    { key: 'solo', label: 'Solo', active: Number(x.solo_active) || 0, total: Number(x.solo_total) || 0, pct: x.solo_pct ?? null },
  ];
}
/** billing.trend → chart data, oldest first, with the live (unfrozen) month flagged. */
export function billingTrendData(trend) {
  return (trend || []).map(t => ({
    month: t.month, label: monthLabel(t.month),
    active_clients: Number(t.active_clients) || 0,
    member_rows: Number(t.member_rows) || 0,
    orgs: Number(t.orgs) || 0,
    frozen: t.frozen !== false,
  }));
}
